import React, { useContext, useState } from 'react';
import { updateEventDetails, Event, EventStatus } from '../../../../../../model/event';
import { Button } from '../../../../../../components/Button/Button';
import { SalesDonut } from '../../../../../../components/SalesDonut/SalesDonut';
import { formatDate } from '../../../../../../util/util';
import { handleError } from '../../../../../../model/notification';
import { isAdmin, useSelectedMembership } from '../../../../../../model/membership';
import { t } from '../../../../../../locales/translate';
import { Stack } from '../../../../../../components/Stack/Stack';
import { ActivitiesContext } from '../../../../../../components/Activities/Activities';
import { useConfirmation } from '../../../../../../components/ConfirmationPrompt/ConfirmationPrompt';

interface Props {
  event: Event;
}

export function StatusOnSale({ event }: Props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const membership = useSelectedMembership();
  const { reloadActivities } = useContext(ActivitiesContext);
  const { confirm } = useConfirmation({
    title: t.actions.stopSale,
    message: t.general.confirmStopSale,
    action: () => setStatus('stopped'),
  });

  async function setStatus(status: EventStatus): Promise<void> {
    setLoading(true);
    try {
      await updateEventDetails({ eventId: event.id, changes: { status } });
      reloadActivities();
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  return (
    <div className="event-status__on-sale">
      <Stack className="event-status__details">
        <div className="event-status__title">
          {event.status === 'on_sale' && t.model.status.onSale}
          {event.status === 'stopped' && event.$permissions.includes('reactivate') && isAdmin(membership) && (
            <>
              <span>{t.model.status.stopped}</span>
              <Button
                onClick={() => setStatus('on_sale')}
                className="reactive-btn"
                title={t.general.reactivate}
                loading={loading}
              />
            </>
          )}
        </div>
        <div>
          {t.model.event.happeningOn} <strong>{formatDate(event.happeningFrom)}</strong>
        </div>
        {event.$permissions.includes('stop_sale') && (
          <div className="event-status__actions">
            <Button loading={loading} title={t.actions.stopSale} type="primary" onClick={confirm} />
          </div>
        )}
      </Stack>
      <div className="event-status__graph">
        {event.salesStats && <SalesDonut legendType="aside" data={event.salesStats} size="medium" />}
        {event.salesStats?.returned !== undefined && (
          <div className="event-status__returned">
            <p>{t.model.sale.returned(event.salesStats.returned || 0)}</p>
          </div>
        )}
      </div>
    </div>
  );
}
